import { CommonModule } from "@angular/common";
import { ChangeDetectorRef, Component, Inject, ViewChild } from "@angular/core";
import { MatPaginator, MatPaginatorIntl, MatPaginatorModule } from "@angular/material/paginator";
import { CustomMatPaginatorIntl } from "app/mock-api/common/customPaginatorIntl";
import { TripAttributesGps, VisitGps } from "../dto/visit-gps.interface";
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from "@angular/material/dialog";
import { MatTabsModule } from "@angular/material/tabs";
import { MarkerData, MarkerGroup } from "app/common/components/geo-manager/geo-manager.interfaces";
import { GeoManagerComponent } from "app/common/components/geo-manager/geo-manager.component";
import { VisitGpsService } from "../visit-gps.service";
import { MatSnackBar } from "@angular/material/snack-bar";
import { MatTableDataSource, MatTableModule } from "@angular/material/table";
import { MatSort, MatSortModule } from "@angular/material/sort";
import { FormatOtherDateHourPipe } from "app/pipes/other-date.pipe";
import { MatIconModule } from "@angular/material/icon";
import { extractCustomModalProps } from "../utils/visit-gps.helper";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatSelectModule } from "@angular/material/select";
import { MatOptionModule } from "@angular/material/core";
import { MatButtonModule } from "@angular/material/button";

@Component({
  selector: "app-map-visit",
  standalone: true,
  imports: [
    CommonModule,
    MatDialogModule,
    MatTabsModule,
    MatTableModule,
    MatPaginatorModule,
    MatSortModule,
    MatButtonModule,
    MatIconModule,
    MatFormFieldModule,
    MatSelectModule,
    MatOptionModule,
    GeoManagerComponent
  ],
  providers: [{ provide: MatPaginatorIntl, useClass: CustomMatPaginatorIntl }],
  templateUrl: "./map-visit.component.html",
  styles: [`
    ::ng-deep .mat-mdc-tab-group .mat-mdc-tab-body-content {
      padding: 10px !important;
    }
  `]
})
export class MapVisitGpsComponent {
  @ViewChild('geoManagerLast', { static: false }) geoManagerLast!: GeoManagerComponent;
  @ViewChild('geoManagerMany', { static: false }) geoManagerMany!: GeoManagerComponent;
  @ViewChild(MatPaginator, { static: false }) paginator!: MatPaginator;
  @ViewChild(MatSort, { static: false }) sort!: MatSort;

  selected = 0;
  selectedInnerTab;
  filteredMarkerLast: MarkerGroup[] = [];
  filteredMarkerMany: MarkerGroup[] = [];

  displayedColumns: string[] = ["location", "latitude", "longitude", "timestamp"];
  dataSourceReplica = new MatTableDataSource<VisitGps>([]);

  replica: number = 10
  listQtReplicas: number[] = [10, 20, 30, 40, 50]

  constructor(
    private visitGpsService: VisitGpsService,
    private snackBar: MatSnackBar,
    private dialogRef: MatDialogRef<MapVisitGpsComponent>,
    @Inject(MAT_DIALOG_DATA) public data: TripAttributesGps
  ) { }

  ngAfterViewInit(): void {
    this.getPointLast();
    this.getManyPoints();
  }

  onTabChange(index: number) {
    this.selected = index;
    setTimeout(() => {
      if (this.geoManagerMany) {
        this.geoManagerMany.reloadMap();
      }
    }, 0);
  }

  onInnerTabChange(index: number) {
    this.selectedInnerTab = index;
  }

  getPointLast() {
    const send = {
      date: this.formatDate(this.data.dateHourStart),
      userid: this.data.user._id,
      limit: 1
    };

    this.visitGpsService.getListReplicas(send).subscribe({
      next: (res: VisitGps[]) => {
        if (res.length > 0) {
          this.getPointsMap(res[0]);
        }
      },
      error: (err) => {
        this.snackBar.open(err.error.message || "Error al obtener visitas", "Cerrar", { duration: 5000 });
      },
    });
  }

  getManyPoints(value?: number) {
    if (value !== undefined) {
      this.replica = value;
    }

    const send = {
      date: this.formatDate(this.data.dateHourStart),
      userid: this.data.user._id,
      limit: this.replica
    };

    this.visitGpsService.getListReplicas(send).subscribe({
      next: (res: VisitGps[]) => {
        if (res.length > 0) {
          this.getPointsMap(res, true);
          this.getTable(res);
        }
      },
      error: (err) => {
        this.snackBar.open(err.error.message || "Error al obtener visitas", "Cerrar", { duration: 5000 });
      },
    });
  }

  getPointsMap(trips: VisitGps | VisitGps[], isMany: boolean = false) {
    const tripArray = Array.isArray(trips) ? trips : [trips];
    const markerArray: MarkerGroup[] = [];

    tripArray.forEach((trip, index) => {
      const customProps = extractCustomModalProps(trip, this.data);
      if (trip.location) {
        let groupColor = '#525252'; // gris
        let groupName = 'Sin información';

        if (trip.orderCode != null) {
          groupColor = '#2a8ed1';
          groupName = 'Ocupado';
        } else if (trip.orderCode == null && trip.location.is_moving) {
          groupColor = '#008000';
          groupName = 'Libre';
        }

        const marker: MarkerData = {
          title: trip.userName,
          latitude: trip.location.coords.latitude,
          longitude: trip.location.coords.longitude,
          customProperties: customProps,
          ...(isMany ? { order: index + 1 } : {}),
        };
        markerArray.push({ groupName, markers: [marker], groupColor });
      }
    });

    if (isMany) {
      this.filteredMarkerMany = markerArray;
    } else {
      this.filteredMarkerLast = markerArray;
    }
  }

  getTable(points: VisitGps[]) {
    const dateHourPipe = new FormatOtherDateHourPipe();
    points.forEach(ele => {
      try {
        ele.timestamp = dateHourPipe.transform(ele.location.timestamp, true);
      } catch (e) {
        ele.timestamp = '';
      }
    });

    this.dataSourceReplica.data = points;
    this.dataSourceReplica.paginator = this.paginator;
    this.dataSourceReplica.sort = this.sort;
  }

  openGoogleMap(element: VisitGps) {
    window.open(`https://maps.google.com/maps?q=${element.location.coords.latitude},${element.location.coords.longitude}`, "_blank");
  }

  private formatDate(dateUtcString: Date): string {
    const date = new Date(dateUtcString);
    const day = String(date.getUTCDate()).padStart(2, '0');
    const month = String(date.getUTCMonth() + 1).padStart(2, '0');
    const year = date.getUTCFullYear();
    return `${day}-${month}-${year}`;
  }
}
